<template>
<!-- eslint-disable max-len -->
<div class="approot" ref="approot">
  <div class="viewport-height-detector" ref="viewportHeightDetector" />
  <div
    ref="app"
    class="app"
    :class="{ 'background-signin': !isGameRoute, 'background-game': isGameRoute }"
  >
    <template v-show="environmentLoaded" @mouseover="mouseover">
      <blade-navbar id="navbar"/>
      <template>
        <transition :enter-active-class="enterActiveClass" :leave-active-class="leaveActiveClass">
          <background
            v-if="isGameRoute"
            id="app-background"
            ref="backgroundgame"
            class="background-game"
            :background="demoBackground"
            :background-color="demoBackgroundColor"
            :slow-zoom="slowZoom"
            animate
            :static-content="staticNarrativeContent"
          >
            <router-view />
            <!-- <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
              <chat v-if="chatVisible"/>
            </transition> -->
            <progress-bar v-if="isGameRoute && !hideProgressBarRoute && (!game || !game.hideProgressBar)"/>
            <b-button
              v-if="isGameRoute && !hideProgressBarRoute && (!game || !game.hideDecisionHistory)"
              :style="notificationPanelButtonStyle"
              @click="() => { showGroupNotificationPanel = !showGroupNotificationPanel; }"
            >
              <span v-if="!showGroupNotificationPanel && (unreadNotificationCount > 0)">
                {{ unreadNotificationCount }}
              </span>
              <img v-else
                src="./assets/ui/InfoOpenIcon.png"
                :style="{
                  height: '21px',
                  width: '70%',
                  'object-fit': 'contain',
                  transform: showGroupNotificationPanel ? 'rotate(-135deg)' : undefined,
                  transition: 'transform 1s',
                }"
              />
            </b-button>
            <transition enter-active-class="animated fadeInRight" leave-active-class="animated fadeOutRight">
              <notification-panel
                v-if="showGroupNotificationPanel && isGameRoute && !hideProgressBarRoute"
                class="notification-panel"
              >
              </notification-panel>
            </transition>
            <div v-if="tooltip && tooltip.preventOtherInputWhileOpen"
              style="background-color: transparent; position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: all; z-index: 998;"
              @click="()=>{}"
            >
            <!-- note: empty click handler needed to bubble a click to the window handler -->
            </div>
            <tooltip>
            </tooltip>
          </background>
          <router-view
            v-else
            id="app-background"
          />
        </transition>
      </template>
      <flash></flash>
      <b-toaster name="b-toaster-top-center" style="margin-top: 35px;"></b-toaster>
    </template>
    <template v-if="!environmentLoaded || rootLoadingMessage">
      <div style="display: flex; flex-flow: column nowrap; justify-content: center; align-items: center; position: fixed; left: 0px; right: 0px; bottom: 0px; top: 0px; background-color: #dddddd; z-index: 1000;">
        <blade-spinner no-background nested/><div style="margin-top: 5px; font-size: 150%;">{{rootLoadingMessage}}</div>
      </div>
    </template>

    <audio ref="audio" :loop="audioLooping" :src="audioPath">
    </audio>

    <!-- global handler for single-play sounds -->
    <sound-manager></sound-manager>

    <!-- fullscreen button that is enabled when BLADE is loaded in an iframe so that it can still use the whole screen -->
    <b-btn
      v-if="enableBrowserFullscreenButton"
      style="position: absolute; bottom: 4px; right: 8px; width: 32px; min-width: 32px; height: 32px;"
      title="Toggle Fullscreen"
      @click.stop="toggleFullscreen"
    >
      <b-icon icon="fullscreen-exit" scale="1.2" class="show-fullscreen"></b-icon>
      <b-icon icon="arrows-fullscreen" scale="1.2" class="hide-fullscreen"></b-icon>
    </b-btn>
  </div>
</div>
</template>

<script>
import Vue from 'vue';
import {
  mapGetters,
  mapActions,
  mapMutations,
  mapState,
} from 'vuex';
import { preloadAssetsFromObject, loadMasterAssetManifest } from '@/utils/asset-util';

import Background from './components/game/ui/Background';
import ProgressBar from './components/game/ui/ProgressBar';
// import Chat from './components/game/ui/Chat';
import BladeNavbar from './components/Navbar';
import Flash from './components/Flash';
import NotificationPanel from './components/game/ui/NotificationPanel';
import Tooltip from './components/game/ui/Tooltip';
import SoundManager from './components/game/pages/components/SoundManager';

import { handleIFrame } from './utils/utils';
import isInIframe from './utils/iframe';
import toggleBrowserFullScreen from './utils/fullscreen';

export default {
  components: {
    // Chat,
    BladeNavbar,
    Flash,
    Background,
    ProgressBar,
    Tooltip,
    SoundManager,
    NotificationPanel
  },
  data() {
    return {
      environmentLoaded: false,
      preloadedImages: {},
      loaded: false,
      demoBackground: '',
      demoBackgroundColor: '',
      showGroupNotificationPanel: false,
      audioStatus: '',
      enableBrowserFullscreenButton: false, // for when BLADE is loaded in an iframe
      isBrowserFullscreenEnabled: false,
    };
  },
  computed: {
    ...mapGetters([
      'muteMusic',
      'volumeMusic',
      'activeEnvironment',
      'activeSignInConfig',
      'game',
      'myPlayer',
      'narrative',
      'background',
      'backgroundColor',
      'chatVisible',
      'slowZoom',
      'staticNarrativeContent',
      'hasAuthToken',
      'playerRole',
      'canManageGames',
      'canManageUsers',
      'showAdmin',
      'tooltip',
      'disableForcedLandscape',
      'rootLoadingMessage',
      'unreadNotificationCount'
    ]),
    ...mapState([
      'audio',
    ]),
    path() {
      return this.$route.path;
    },
    enterActiveClass() {
      return this.isGameRoute && this.loaded ? 'animated fadeIn' : '';
    },
    leaveActiveClass() {
      return this.isGameRoute && this.loaded ? 'animated fadeOut' : '';
    },
    isGameRoute() {
      const { path, meta } = this.$route;

      return path.startsWith('/demo') || path.startsWith('/groups') || (meta && meta.isGameRoute);
    },
    hideProgressBarRoute() {
      const { meta } = this.$route;
      if (!meta) return false;
      return meta.hideProgressBar;
    },
    playerRoleBackground() {
      return this.playerRole.background;
    },
    audioPath() {
      if (this.narrative && this.narrative.backgroundAudioChange) {
        return this.narrative.backgroundAudioChange;
      } else if (this.myPlayer && this.myPlayer.currentBackgroundAudio) {
        return this.myPlayer.currentBackgroundAudio;
      } else if (this.game && this.game.audioSelectBackground) {
        return this.game.audioSelectBackground;
      }

      return '';
    },
    audioLooping() {
      if (this.narrative && this.narrative.backgroundAudioChange && this.narrative.backgroundAudioLoopingChange != null) {
        return this.narrative.backgroundAudioLoopingChange;
      } else if (this.myPlayer && this.myPlayer.currentBackgroundAudio && this.myPlayer.currentBackgroundAudioLooping != null) {
        return this.myPlayer.currentBackgroundAudioLooping;
      } else if (this.game && this.game.isAudioBackgroundLooping != null) {
        return this.game.isAudioBackgroundLooping;
      }

      return false;
    },
    audioVolume() {
      if (this.narrative && this.narrative.backgroundAudioVolumeChange) {
        return this.narrative.backgroundAudioVolumeChange;
      } else if (this.myPlayer && this.myPlayer.currentBackgroundAudioVolume) {
        return this.myPlayer.currentBackgroundAudioVolume;
      } else if (this.game && this.game.backgroundAudioVolume) {
        return this.game.backgroundAudioVolume;
      }

      return 100;
    },
    audioPlay() {
      return this.audio.isAudioPlay;
    },
    audioPause() {
      return this.audio.isAudioPause;
    },
    notificationPanelButtonStyle() {
      return {
        'z-index': '10000',
        'font-size': '20px',
        'font-weight': 'bold',
        'min-width': '40px',
        'max-height': '27px',
        width: '40px',
        padding: '0',
        position: 'absolute',
        bottom: '20px',
        right: '6px',
        filter: (this.unreadNotificationCount) ? 'drop-shadow(0 0 8px red)' : 'unset'
      };
    }
  },
  watch: {
    async path(newPath, oldPath) {
      document.documentElement.style.setProperty('--body-position-setting', this.isGameRoute ? 'fixed' : 'unset');
      // Requires 2 ticks for background to propogate through store
      // Then an additional 2 ticks for any potential narrative background to get loaded
      // (in boundary cases when the state then the narrative is switched over in the store)
      await Vue.nextTick();
      await Vue.nextTick();
      await Vue.nextTick();
      await Vue.nextTick();
      this.demoBackground = this.background(this.path);
      this.demoBackgroundColor = this.backgroundColor(this.path);

      if (this.isGameRoute) this.detectViewportHeight();

      if ((newPath === '/' || newPath.includes('/hub'))
        && !(oldPath === '/' || oldPath.includes('/hub'))
        && /mobi|android/i.test(navigator.userAgent)) {
        // fixes some order of operations issue that causes the forced landscape mode to not activate on sign in until you refresh
        // TODO: it would be nice to fix the root cause and remove this fix, but I'm not sure we have enough control to do that
        setTimeout(() => { this.$router.go(0); }, 50);
      }
    },
    async background() {
      // Requires 2 ticks for background to propogate through store
      // Then an additional 2 ticks for any potential narrative background to get loaded
      // (in boundary cases when the state then the narrative is switched over in the store)
      await Vue.nextTick();
      await Vue.nextTick();
      await Vue.nextTick();
      await Vue.nextTick();
      this.demoBackground = this.background(this.path);
    },
    async playerRoleBackground() {
      // Requires 2 ticks for background to propogate through store
      // Then an additional 2 ticks for any potential narrative background to get loaded
      // (in boundary cases when the state then the narrative is switched over in the store)
      await Vue.nextTick();
      await Vue.nextTick();
      await Vue.nextTick();
      await Vue.nextTick();
      this.demoBackground = this.background(this.path);
      this.demoBackgroundColor = this.backgroundColor(this.path);
    },
    audioPath(newValue, oldValue) {
      if (newValue !== oldValue) {
        setTimeout(() => this.handleAudio(), 10);
      }
    },
    audioVolume(newValue) {
      // console.log(newValue);
      if (this.audioBackground && newValue) {
        if (this.muteMusic) {
          this.audioBackground.volume = 0;
        } else {
          this.audioBackground.volume = Math.max(0, (newValue / 100.0) * ((this.volumeMusic / 100.0) + 1.0));
        }
      }
    },
    audioPlay(newValue) {
      if (newValue) this.handleAudio();
    },
    audioPause(newValue) {
      if (newValue && this.audioStatus === 'play') {
        this.audioBackground.pause();
        this.audioStatus = 'pause';
      } else if (!newValue) {
        this.handleAudio();
      }
    },
    disableForcedLandscape() {
      // console.log(this.disableForcedLandscape);
      this.setForcedLandscape();
    },
    muteMusic(newValue) {
      if (!this.audioBackground) this.audioBackground = this.$refs.audio;
      if (this.audioBackground) {
        if (newValue) this.audioBackground.volume = 0;
        else this.audioBackground.volume = Math.max(0, (this.audioVolume / 100.0) * ((this.volumeMusic / 100.0) + 1.0));
      }
    },
    volumeMusic(newValue) {
      if (!this.audioBackground) this.audioBackground = this.$refs.audio;
      if (this.audioBackground) {
        this.audioBackground.volume = Math.max(0, (this.audioVolume / 100.0) * ((newValue / 100.0) + 1.0));
      }
    },
  },
  async mounted() {
    this.enableBrowserFullscreenButton = isInIframe();
    loadMasterAssetManifest();
    /*
    ['backgrounds', 'characters', 'images'].forEach((assetType) => {
      manifest[assetType].forEach((asset) => {
        const image = new Image();
        image.src = `/static/${assetType}/${asset}`;
        this.preloadedImages[asset] = image;
      });
    }); */
    // hide resize on the background element
    await new Promise(resolve => setTimeout(resolve, 1000));
    this.detectViewportHeight();
    this.setForcedLandscape();
    this.loaded = true;
    this.audioBackground = this.$refs.audio;
    window.addEventListener('click', this.initAudio);

    if (this.path.startsWith('/groups')) this.handleAudio();
  },
  methods: {
    ...mapMutations([
      'setActiveEnvironment',
      'setActiveSignInConfig',
      'setAudioPlaying',
      'setAudioPause',
    ]),
    ...mapActions([
      'getActiveEnvironment',
      'getActiveSignInConfig',
      'flashPrimary',
      'flashWarning',
      'setTooltips',
      'loadAssetData',
    ]),
    detectViewportHeight() {
      // console.log(this.$refs.viewportHeightDetector.clientHeight);
      // this.flashPrimary({ message: `${this.$refs.viewportHeightDetector.clientHeight}` });
      const detectedViewportHeight =
        (this.$refs.viewportHeightDetector && this.$refs.viewportHeightDetector.clientHeight)
          ? `${this.$refs.viewportHeightDetector.clientHeight}px`
          : '100vh';
      document.documentElement.style.setProperty('--detected-viewport-height', detectedViewportHeight);
    },
    setForcedLandscape() {
      document.documentElement.classList.remove('forced-landscape');
      document.documentElement.classList.remove('disable-forced-landscape');
      document.body.classList.remove('forced-landscape');
      document.body.classList.remove('disable-forced-landscape');
      this.$refs.approot.classList.remove('forced-landscape');
      this.$refs.approot.classList.remove('disable-forced-landscape');
      this.$refs.app.classList.remove('forced-landscape');
      this.$refs.app.classList.remove('disable-forced-landscape');
      if (!(this.disableForcedLandscape || isInIframe())) {
        document.documentElement.classList.add('forced-landscape');
        document.body.classList.add('forced-landscape');
        this.$refs.approot.classList.add('forced-landscape');
        this.$refs.app.classList.add('forced-landscape');
      } else {
        document.documentElement.classList.add('disable-forced-landscape');
        document.body.classList.add('disable-forced-landscape');
        this.$refs.approot.classList.add('disable-forced-landscape');
        this.$refs.app.classList.add('disable-forced-landscape');
      }
      if (this.$refs.backgroundgame) {
        this.$refs.backgroundgame.handleResize();
      }
    },
    handleAudio() {
      window.dispatchEvent(new Event('click'));
      if (
        this.audioBackground
        && this.audioPlay
      ) {
        // console.log(this.audioVolume);
        // console.log(this.volumeMusic);
        if (this.muteMusic) {
          this.audioBackground.volume = 0;
        } else if (this.audioVolume || this.volumeMusic) {
          this.audioBackground.volume = Math.max(0, (this.audioVolume / 100.0) * ((this.volumeMusic / 100.0) + 1.0));
        }
        this.audioBackground
          .play()
          .then(() => {
            this.setAudioPause(false);
            this.setAudioPlaying(true);
            this.audioStatus = 'play';
          })
          .catch(() => {
            this.setAudioPause(true);
            this.setAudioPlaying(false);
          });
      }
    },
    initAudio() {
      // necessary to fix background music playing on Safari and iOS
      if (this.audioPath && this.audioBackground && this.audioPlay) {
        this.audioBackground.play().catch(() => {
          this.flashWarning({ message: 'Some audio was unable to play. Check your browser settings for "prevent autoplay".' });
        });
        window.removeEventListener('click', this.initAudio);
      }
    },
    toggleFullscreen() {
      this.isBrowserFullscreenEnabled = toggleBrowserFullScreen();
    },
  },
  async created() {
    window.addEventListener('resize', this.detectViewportHeight);
    window.addEventListener('scroll', this.detectViewportHeight);
    window.handleIFrame = handleIFrame;
    try {
      // TODO: replace with something more reliable like a watch on the permissions?
      setTimeout(() => {
        if ((this.canManageGames || this.canManageUsers) && this.showAdmin) {
          this.loadAssetData();
        }
      }, 500);

      try {
        await this.getActiveEnvironment();
      } catch (err) {
        console.error('Failed to load active environment');
        console.error(err);
        this.setActiveEnvironment();
      }
      try {
        // Don't do this for sign up links - let them get it themselves
        if (!this.$route.path.startsWith('/signup/link/')) {
          await this.getActiveSignInConfig();
        }
      } catch (err) {
        console.error('Failed to load sign in config');
        console.error(err);
        await this.setActiveSignInConfig();
      }
      // await Promise.all(loads);

      await Promise.all([
        preloadAssetsFromObject(this.activeEnvironment),
        preloadAssetsFromObject(this.activeSignInConfig),
      ]);

      // Wait for css transitions to complete
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (er) {
      console.error(er);
    }
    this.environmentLoaded = true;
  },
  destroyed() {
    window.removeEventListener('resize', this.detectViewportHeight);
    window.removeEventListener('scroll', this.detectViewportHeight);
    window.handleIFrame = undefined;
  }
};
</script>

<style lang="scss">
@media screen and (min-width: 320px) and (max-width: 767px) and (orientation: portrait) {
  // fix for the somehow-always-broken-for-some-reason iOS...
  // in iOS browsers, stuff at the same z-coordinate will sometimes render on top of rotated stuff
  // unless you explicitly move it back with a transform
  // and doing so will break the scaling of the layout unless you add height: 100%
  // ... it baffles me how Safari is always somehow as broken as IE.
  .approot.forced-landscape {
    -webkit-transform: translate3d(0, 0, -1px);
    height: 100%;
  }

  .app.background-game.forced-landscape {
    transform: rotate(-90deg);
    transform-origin: left top;
    width: var(--detected-viewport-height);
    height: 100vw;
    overflow-x: visible;
    position: absolute;
    top: var(--detected-viewport-height);
    left: 0;

    .navbar-root.forced-landscape {
      width: 100% !important;
    }
  }

  body.forced-landscape {
    height: 100%;
    position: var(--body-position-setting);
  }
}

@media screen and (orientation: landscape) {
  html {
    height: 100%;
    overflow: visible;
  }

  .app.background-game {
    height: 100vh;
    overflow: visible;
  }
}

html.disable-forced-landscape {
  height: 100%;
  overflow: visible;
}

.app.background-game.disable-forced-landscape {
  height: 100vh;
  overflow: visible;
}

.viewport-height-detector {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  pointer-events: none;
}

/*
 * The Typekit service used to deliver this font or fonts for use on websites
 * is provided by Adobe and is subject to these Terms of Use
 * http://www.adobe.com/products/eulas/tou_typekit. For font license
 * information, see the list below.
 *
 * aller:
 *   - http://typekit.com/eulas/000000000000000077359d6c
 *   - http://typekit.com/eulas/000000000000000077359d73
 *   - http://typekit.com/eulas/000000000000000077359d76
 *   - http://typekit.com/eulas/000000000000000077359d78
 *   - http://typekit.com/eulas/000000000000000077359d7a
 *   - http://typekit.com/eulas/000000000000000077359d7b
 *
 * © 2009-2021 Adobe Systems Incorporated. All Rights Reserved.
 */
/*{"last_published":"2021-04-07 14:33:42 UTC"}*/

@import url("https://p.typekit.net/p.css?s=1&k=ovj6des&ht=tk&f=9763.9764.9765.9766.9767.9768&a=6960253&app=typekit&e=css");

@font-face {
font-family: "Aller";
src:url("https://use.typekit.net/af/2c88fb/000000000000000077359d6c/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff2"),url("https://use.typekit.net/af/2c88fb/000000000000000077359d6c/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff"),url("https://use.typekit.net/af/2c88fb/000000000000000077359d6c/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:400;
}

@font-face {
font-family: "Aller";
src:url("https://use.typekit.net/af/c9d9c8/000000000000000077359d73/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff2"),url("https://use.typekit.net/af/c9d9c8/000000000000000077359d73/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff"),url("https://use.typekit.net/af/c9d9c8/000000000000000077359d73/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:400;
}

@font-face {
font-family: "Aller";
src:url("https://use.typekit.net/af/dbb590/000000000000000077359d76/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff2"),url("https://use.typekit.net/af/dbb590/000000000000000077359d76/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff"),url("https://use.typekit.net/af/dbb590/000000000000000077359d76/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:700;
}

@font-face {
font-family: "Aller";
src:url("https://use.typekit.net/af/676c27/000000000000000077359d78/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff2"),url("https://use.typekit.net/af/676c27/000000000000000077359d78/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff"),url("https://use.typekit.net/af/676c27/000000000000000077359d78/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:700;
}

@font-face {
font-family: "Aller";
src:url("https://use.typekit.net/af/7e3b66/000000000000000077359d7a/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n3&v=3") format("woff2"),url("https://use.typekit.net/af/7e3b66/000000000000000077359d7a/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n3&v=3") format("woff"),url("https://use.typekit.net/af/7e3b66/000000000000000077359d7a/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n3&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:300;
}

@font-face {
font-family: "Aller";
src:url("https://use.typekit.net/af/83a63b/000000000000000077359d7b/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i3&v=3") format("woff2"),url("https://use.typekit.net/af/83a63b/000000000000000077359d7b/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i3&v=3") format("woff"),url("https://use.typekit.net/af/83a63b/000000000000000077359d7b/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i3&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:300;
}

.tk-aller { font-family: "Aller", sans-serif; }

body {
  font-family: var(--defaultFont), $font-family !important;
  background-color: var(--secondaryColor);
}

.background-signin {
  background-image: var(--signInBackground);
  background-size: cover;
  background-repeat: no-repeat;
  background-color: var(--primaryColor);
  // min-height: 900px;
  min-height: 100vh;
}
.background-game {
  background-image: var(--gameBackgroundImage);
  background-size: cover;
  background-repeat: no-repeat;

  // background-color: white;

  background-color: var(--neutralColor);

  // min-height: 900px;
}

.background-light {
  background-color: white;
}
.background-dark {
  background-color: rgb(15, 15, 15);
}
.form-control {
  @include form-control();
}
.error-msg {
  @include error-msg();
}
input.error, select.error {
  @include input-error();
}
.form-control:focus {
  border-color: #9b9b9b;
  box-shadow: 0 0 0 0.1rem #9b9b9b;
}
.currency::before {
  content: '$';
  font-size: 20px;
  line-height: 0;
}
.form-image-background {
  background-color: #aaaaaa;
  max-height: 350px;
  max-width: 250px;
  margin: 10px;
}
.form-image-character {
  background-color: #aaaaaa;
  max-height: 350px;
  max-width: 150px;
  margin: 10px;
}
.form-video {
  background-color: black;
  max-width: 350px;
  max-height: 250px;
  margin: 10px;
}

// btn
.btn {
  @include btn();
}
.btn:focus {
  @include btn-focus();
}
.btn:hover:not([disabled]):not(:disabled):not(.disabled),
.btn:active:not([disabled]):not(:disabled):not(.disabled) {
  @include btn-hover();
}

// btn-choice
.btn-choice {
  @include btn-choice();
}
.btn-choice:focus {
  @include btn-focus-choice();
}
.btn-choice:hover:not([disabled]):not(:disabled):not(.disabled),
.btn-choice:active:not([disabled]):not(:disabled):not(.disabled) {
  @include btn-hover-choice();
}

// btn-faq-category
.btn-faq-category {
  @include btn-faq-category();
}
.btn-faq-category:focus {
  @include btn-focus-faq-category();
}
.btn-faq-category:hover:not([disabled]):not(:disabled):not(.disabled),
.btn-faq-category:active:not([disabled]):not(:disabled):not(.disabled) {
  @include btn-hover-faq-category();
}
.btn-faq-category-unselected {
  @include btn-faq-category-unselected();
}
.btn-faq-category-unselected:focus {
  @include btn-focus-faq-category();
}
.btn-faq-category-unselected:hover:not([disabled]):not(:disabled):not(.disabled),
.btn-faq-category-unselected:active:not([disabled]):not(:disabled):not(.disabled) {
  @include btn-hover-faq-category();
}

// btn-faq-article
.btn-faq-article {
  @include btn-faq-article();
}
.btn-faq-article:focus {
  @include btn-focus-faq-article();
}
.btn-faq-article:hover:not([disabled]):not(:disabled):not(.disabled),
.btn-faq-article:active:not([disabled]):not(:disabled):not(.disabled) {
  @include btn-hover-faq-article();
}
.btn-faq-article-unselected {
  @include btn-faq-article-unselected();
}
.btn-faq-article-unselected:focus {
  @include btn-focus-faq-article();
}
.btn-faq-article-unselected:hover:not([disabled]):not(:disabled):not(.disabled),
.btn-faq-article-unselected:active:not([disabled]):not(:disabled):not(.disabled) {
  @include btn-hover-faq-article();
}

.btn-sm {
  padding: 9px;
  margin: 0;
}

// TODO: I think most if not all of the stuff below can be removed
.btn-btn {
  @include btn-btn();
}
.btn:focus .btn-btn,
.btn-btn:focus {
  @include btn-btn-focus();
}
.btn:hover:not([disabled]):not(:disabled):not(.disabled) .btn-btn,
.btn:active:not([disabled]):not(:disabled):not(.disabled) .btn-btn,
.btn-btn:hover:not([disabled]):not(:disabled):not(.disabled),
.btn-btn:active:not([disabled]):not(:disabled):not(.disabled) {
  @include btn-btn-hover();
}
.btn-white {
  @include btn-white();
}
// a.btn {
//   color: white;
// }
// use in addition to main btn class
.btn-info{
  border-radius: 50%;
  width: 50px;
  height: 50px;
  line-height: 30px;
  font-size: 40px;
  min-width: 40px;
  min-height: 40px;
}
.btn-info:focus, .btn-info:hover {
  border-color: var(--primaryColor);
  border-radius: 50%;
}
.notification-panel {
  position: absolute;
  bottom: 65px;
  right: 1px;
  background-color: rgba(0, 0, 0, 0.65) !important;
  z-index: 15000;
}

// This CSS handles swapping the fullscreen button icon depending on display-mode.
// Used instead of isBrowserFullscreenEnabled because use of hotkeys like F11 in Chrome cannot be detected.

.show-fullscreen {
  display: none !important;
}

@media (display-mode: fullscreen) {
  .show-fullscreen {
    display: unset !important;
  }

  .hide-fullscreen {
    display: none !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue